import type { Item } from "./types";

const MS_DAY = 86_400_000;

export function startOfDay(d = new Date()): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/**
 * Parse a stored date as a LOCAL calendar day.
 *
 * `start` and `due` are calendar days, not instants — "the 5th" means the 5th
 * wherever you are. But they live in PocketBase `date` columns, so a stored
 * "2026-09-05" comes back as "2026-09-05 00:00:00.000Z". Parsing that directly
 * yields UTC midnight, which is 5pm on the 4th in Pacific — so everything
 * scheduled for today read as one day overdue.
 *
 * Taking the leading Y-M-D and rebuilding it locally makes the round-trip
 * lossless in every timezone. Anything without a leading date falls back to
 * normal parsing, so real timestamps still work.
 */
export function parseDate(s?: string): Date | null {
  if (!s) return null;
  const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (ymd) return new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

/** "Aug 24" */
export function shortDate(s?: string): string {
  const d = parseDate(s);
  if (!d) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** "Tuesday, August 18, 2026" */
export function longDate(d = new Date()): string {
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Human due label relative to today: "Today", "3 days late", "ends Oct 23".
 * `overdue` is date-only (in the past); `soon` marks the near horizon — due
 * today through two days out — so callers can give it an urgent-but-not-late
 * treatment. The two flags are disjoint.
 */
// "Due soon" window in days, set from Settings › Planning defaults (§11).
let dueSoonDays = 3;
export function setDueSoonDays(days: number): void { dueSoonDays = days; }
export function getDueSoonDays(): number { return dueSoonDays; }

export function dueLabel(s?: string, prefix = "", status?: string): { text: string; overdue: boolean; soon: boolean } {
  const d = parseDate(s);
  if (!d) return { text: "", overdue: false, soon: false };
  // A completed item is never "late" — show its date plainly. isOverdue()
  // already ignores done items; dueLabel() must agree, or finished work reads
  // "16 days late" forever. Callers pass item.status to opt in.
  if (status === "done") return { text: prefix + shortDate(s), overdue: false, soon: false };
  const today = startOfDay();
  const due = startOfDay(d);
  const days = Math.round((due.getTime() - today.getTime()) / MS_DAY);
  const soon = days >= 0 && days <= dueSoonDays;
  if (days === 0) return { text: "Today", overdue: false, soon: true };
  if (days < 0) {
    const n = Math.abs(days);
    return { text: `${n} day${n === 1 ? "" : "s"} late`, overdue: true, soon: false };
  }
  if (days === 1) return { text: "Tomorrow", overdue: false, soon: true };
  return { text: prefix + shortDate(s), overdue: false, soon };
}

/** A week after `due`, or a week from today when the item has no date (§15.1). */
export function plusWeek(due?: string): string {
  const base = parseDate(due) || startOfDay();
  const d = new Date(base);
  d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 10);
}

/**
 * The day a task belongs on.
 *
 * Todoist has TWO dates and they are not interchangeable. Its `date` is when
 * you plan to work on something (mirrored to `Item.start`); its `deadline` is
 * the hard finish (mirrored to `Item.due`). Today, Upcoming and the Calendar
 * are about the former — most tasks have no deadline at all, so keying those
 * views off the deadline hides nearly everything you actually scheduled.
 *
 * The fallback to the deadline is deliberate: a task given only a deadline
 * still has to appear somewhere rather than being stranded off-calendar.
 */
export function scheduledFor(item: Pick<Item, "start" | "due">): string | undefined {
  return item.start || item.due;
}

/** The hard finish date, if any. Always the deadline — never the scheduled day. */
export function deadlineOf(item: Pick<Item, "due">): string | undefined {
  return item.due;
}

export type DeadlineState = "overdue" | "today" | "soon" | "later";

/**
 * How urgent a task's DEADLINE is, independent of when it is scheduled. A task
 * can be scheduled for next week and still have a deadline that passed
 * yesterday, which is precisely the situation worth surfacing.
 */
export function deadlineState(item: Pick<Item, "due" | "status">): DeadlineState | null {
  if (item.status === "done") return null;
  const d = parseDate(item.due);
  if (!d) return null;
  const days = Math.round((startOfDay(d).getTime() - startOfDay().getTime()) / MS_DAY);
  if (days < 0) return "overdue";
  if (days === 0) return "today";
  return days <= dueSoonDays ? "soon" : "later";
}

/**
 * Late means the day you planned to do it has passed — which is what Todoist
 * itself shows in red. A missed DEADLINE is a separate, louder signal; see
 * deadlineState().
 */
export function isOverdue(item: Pick<Item, "start" | "due" | "status">): boolean {
  if (item.status === "done") return false;
  const d = parseDate(scheduledFor(item));
  return !!d && startOfDay(d) < startOfDay();
}

export function isToday(s?: string): boolean {
  const d = parseDate(s);
  return !!d && startOfDay(d).getTime() === startOfDay().getTime();
}

export function isTomorrow(s?: string): boolean {
  const d = parseDate(s);
  if (!d) return false;
  const t = startOfDay();
  t.setDate(t.getDate() + 1);
  return startOfDay(d).getTime() === t.getTime();
}

/** [start, end) of the current week, Monday-based, matching the dashboard. */
export function thisWeekRange(): [Date, Date] {
  const now = startOfDay();
  const dow = (now.getDay() + 6) % 7; // Mon=0
  const start = new Date(now.getTime() - dow * MS_DAY);
  const end = new Date(start.getTime() + 7 * MS_DAY);
  return [start, end];
}

export function nextWeekRange(): [Date, Date] {
  const [, end] = thisWeekRange();
  return [end, new Date(end.getTime() + 7 * MS_DAY)];
}

export function pct(done: number, total: number): number {
  return total === 0 ? 0 : Math.round((done / total) * 100);
}
